import React, { useRef, useEffect } from "react";
import { Card } from 'reactstrap';
import Background from './bg';
import { TrendingUp, TrendingDown } from 'lucide-react';

const getDaysAgo = (days) => {
  const today = new Date();
  const sevenDaysAgo = new Date(today);
  sevenDaysAgo.setDate(today.getDate() - days);
  sevenDaysAgo.setHours(0, 0, 0, 0);
  return sevenDaysAgo;
};

// Mini sparkline component
const Sparkline = ({ data, positive }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!canvasRef.current || data.length === 0) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;

    // Clear canvas
    ctx.clearRect(0, 0, width, height);

    // Get min/max for scaling
    const values = data.map(d => d.value);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min || 1;

    // Draw the sparkline
    ctx.beginPath();
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.8)';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    data.forEach((point, i) => {
      const x = (i / (data.length - 1)) * width;
      const y = height - ((point.value - min) / range) * (height - 8) - 4;
      if (i === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    });
    ctx.stroke();

    // Add gradient fill under the line
    const gradient = ctx.createLinearGradient(0, 0, 0, height);
    gradient.addColorStop(0, 'rgba(255, 255, 255, 0.2)');
    gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');

    ctx.lineTo(width, height);
    ctx.lineTo(0, height);
    ctx.closePath();
    ctx.fillStyle = gradient;
    ctx.fill();

  }, [data, positive]);

  return (
    <canvas
      ref={canvasRef}
      width={100}
      height={40}
      className="opacity-80"
    />
  );
};

const InsightsCard = ({ data, loading }) => {

  if (loading) {
    return <React.Fragment />;
  }

  const currentRangeStart = new Date(getDaysAgo(7)).getTime();
  const previousRangeStart = new Date(getDaysAgo(14)).getTime();

  let currentTotal = 0;
  let previousTotal = 0;

  // Get last 7 days data for sparkline
  const last7DaysData = [];

  for (const item of data) {
    const itemTime = new Date(item.item_date).getTime();

    if (itemTime >= previousRangeStart && itemTime < currentRangeStart) {
      previousTotal += item.item_total;
    }

    if (itemTime >= currentRangeStart) {
      currentTotal += item.item_total;
      last7DaysData.push({ date: item.item_date, value: item.item_total });
    }
  }

  if (currentTotal === 0 || previousTotal === 0) {
    return <React.Fragment />;
  }

  const itemValue = (((currentTotal / previousTotal) - 1) * 100);
  const isPositive = itemValue > 0;

  const upClass = 'from-emerald-600 via-emerald-700 to-emerald-800';
  const downClass = 'from-rose-600 via-rose-700 to-rose-800';

  return (
    <Card
      className={`bg-gradient-to-br ${isPositive ? upClass : downClass} text-white mb-2 relative overflow-hidden rounded-xl`}
    >
      <Background className="absolute top-0 left-0 w-100 h-100" opacity={0.08} />

      {/* Decorative circles */}
      <div className="absolute -top-12 -right-12 w-32 h-32 bg-white/10 rounded-full" />
      <div className="absolute -bottom-8 -left-8 w-24 h-24 bg-white/5 rounded-full" />

      <div className="absolute top-0 right-0 border-l border-b border-white/30 bg-black/20 backdrop-blur-sm rounded-bl-xl px-4 py-1.5 text-xs font-medium tracking-wide">
        7-Day Trend
      </div>

      <div className="relative z-10 h-100 p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <p className="text-white/70 text-xs font-medium uppercase tracking-wider mb-3">
              Processing Volume
            </p>
            <div className="flex items-center gap-2 mb-3">
              {isPositive ? (
                <TrendingUp className="w-6 h-6 text-white" />
              ) : (
                <TrendingDown className="w-6 h-6 text-white" />
              )}
              <span className="text-3xl font-bold">{Math.abs(itemValue).toFixed(1)}%</span>
            </div>
            <p className="text-white/80 text-sm">
              {isPositive ? 'Up' : 'Down'} from last week
            </p>
          </div>

          <div className="ml-4">
            <Sparkline data={last7DaysData} positive={isPositive} />
          </div>
        </div>
      </div>
    </Card>
  );
};

export default InsightsCard;
